import React, { useMemo, useState } from "react";
import { FieldGroupRow, FieldGroupRowStatus, FIELD_GROUP_ROW_SORT_TIER, isFieldGroupRowAttention } from "../../../models/intake";
import "./QuickEntryFieldGroupList.scss";

// master-demo-v6.html's own 4 row-status icons (.qe-row__icon, a SEPARATE
// element from .qe-row__badge's numbered circle - not a replacement for
// it). Colors/paths ported 1:1 from the demo's inline SVGs; icon-check/
// icon-warn/icon-stop use currentColor + a CSS color class (QuickEntryFieldGroupList.scss),
// icon-user's blue/white are baked into its own paths in the demo, kept as-is.
const ICON_CHECK_SVG = (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM6.23828 8.52246L3.9541 6.23828L3.19238 7L6.23828 10.0459L10.8076 5.47656L10.0459 4.71484L6.23828 8.52246Z" fill="currentColor" />
    </svg>
);
const ICON_WARN_SVG = (
    <svg viewBox="0 0 15 13" fill="none" aria-hidden="true">
        <path d="M6.40961 0.503857C6.79351 -0.167955 7.76204 -0.16795 8.14594 0.503857L14.4233 11.4882C14.804 12.1548 14.3227 12.9841 13.5551 12.9843H1.00141C0.233587 12.9843 -0.247701 12.1549 0.133246 11.4882L6.40961 0.503857ZM6.70649 10.0761V11.2187H7.84907V10.0761H6.70649ZM6.70649 3.60835V9.03804H7.84907V3.60835H6.70649Z" fill="currentColor" />
    </svg>
);
const ICON_STOP_SVG = (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM6.46191 9.69238V10.7695H7.53809V9.69238H6.46191ZM6.46191 3.23047V8.61523H7.53809V3.23047H6.46191Z" fill="currentColor" />
    </svg>
);
const ICON_USER_SVG = (
    <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="11" fill="#5DA3E3" />
        <path d="M11 11C12.6569 11 14 9.6569 14 8C14 6.3431 12.6569 5 11 5C9.3431 5 8 6.3431 8 8C8 9.6569 9.3431 11 11 11ZM11 12.5C9 12.5 5 13.5 5 15.5V17H17V15.5C17 13.5 13 12.5 11 12.5Z" fill="white" />
    </svg>
);

/**
 * Confirmed splits into two distinct icons (mockup's own icon-check vs
 * icon-user): green check when the value is still the original AI
 * extraction (isAiPreSelected), blue "user" icon once the specialist has
 * entered or edited it themselves. NeedsEntry/LowConfidence map 1:1 to the
 * mockup's red icon-stop/amber icon-warn. EmptyNotRequired (a row that's
 * either genuinely optional or not yet applicable per
 * DataEntryDetails.tsx's isQuickEntryRowApplicable) has no icon in the
 * mockup - the badge number alone is enough for a row that isn't asking
 * for attention.
 */
function getQuickEntryRowIcon(row: FieldGroupRow): { className: string; ariaLabel: string; svg: React.ReactNode } | null {
    switch (row.status) {
        case FieldGroupRowStatus.Confirmed:
            return row.isAiPreSelected
                ? { className: "icon-check", ariaLabel: "Confirmed", svg: ICON_CHECK_SVG }
                : { className: "icon-user", ariaLabel: "User-entered value", svg: ICON_USER_SVG };
        case FieldGroupRowStatus.LowConfidence:
            return { className: "icon-warn", ariaLabel: "Low confidence - click to confirm", svg: ICON_WARN_SVG };
        case FieldGroupRowStatus.NeedsEntry:
            return { className: "icon-stop", ariaLabel: "Needs entry", svg: ICON_STOP_SVG };
        default:
            return null;
    }
}

export interface QuickEntryFieldGroupListProps {
    readonly rows: readonly FieldGroupRow[];
    /** Default ON/OFF state for the "Needs attention first" sort toggle (AC7). */
    readonly sortByAttentionDefault?: boolean;
    /**
     * Optional per-row content override (AC11). When provided and it returns
     * a non-null/non-undefined node for a given row, that node renders in
     * place of the generic label/value/chip trio - the badge number and the
     * surrounding <li> wrapper (and therefore numbering/reorder/sort
     * behavior) are unaffected. Sibling specs (F1b-rois #34100, F1c #34101,
     * F3 #34099) that don't pass this prop get the generic display
     * unchanged - this is purely additive to the FieldGroupRow contract.
     * Used by DataEntryDetails.tsx to mount the real, interactive
     * RequestTypePurposeField for the Request Type/Purpose row instead of
     * its read-only label/value text, so both the classic RightPane and the
     * Quick Entry row render the same shared component (AC11).
     */
    readonly renderRowContent?: (row: FieldGroupRow) => React.ReactNode;
    /**
     * master-demo-v6.html's <footer class="action-footer"> - a direct child
     * of the Quick Entry panel, sibling of .qe__header and the row list
     * (confirmed via DOM inspection: it closes before </main> does). Passed
     * in by the caller (DataEntryDetails.tsx mounts QuickEntryActionFooter
     * here) rather than owned by this generic list component.
     */
    readonly footer?: React.ReactNode;
    /**
     * Optional per-row adornment (additive, same pattern as renderRowContent/footer). Rendered as
     * the last child of each row's <li>, after the content. Sibling spec F1b-rois (#34100) uses it
     * to mount its confidence dot + grounding affordance; specs that don't pass it are unaffected.
     */
    readonly renderRowAdornment?: (row: FieldGroupRow) => React.ReactNode;
}

/**
 * Shared Quick Entry field-group list renderer (Spec #34098 / F1a, AC5-AC10).
 * Sibling specs (F1b-rois #34100, F1c #34101, F3 #34099) implement their own
 * row components against the FieldGroupRow contract this list renders.
 */
const QuickEntryFieldGroupList: React.FC<QuickEntryFieldGroupListProps> = (props) => {
    const [sortByAttention, setSortByAttention] = useState(props.sortByAttentionDefault ?? false);

    const orderedRows = useMemo(() => {
        if (!sortByAttention) {
            return props.rows;
        }
        // Stable sort by tier, preserving original document order within a tier (Business Rule 8).
        return props.rows
            .map((row, index) => ({ row, index }))
            .sort((a, b) => {
                const tierDiff = FIELD_GROUP_ROW_SORT_TIER[a.row.status] - FIELD_GROUP_ROW_SORT_TIER[b.row.status];
                return tierDiff !== 0 ? tierDiff : a.index - b.index;
            })
            .map(entry => entry.row);
    }, [props.rows, sortByAttention]);

    // Badge numbers renumber contiguously (1..N) in whatever order is currently rendered (AC6).
    const numberedRows = useMemo(
        () => orderedRows.map((row, index) => ({ ...row, badgeNumber: index + 1 })),
        [orderedRows],
    );

    const ready = props.rows.filter(row => !isFieldGroupRowAttention(row)).length;
    const attn = props.rows.filter(isFieldGroupRowAttention).length;

    const onToggleSort = () => setSortByAttention(prev => !prev);

    return (
        <div className="qe-shell">
            <div className="qe__header">
                <div className="qe__header-top">
                    <h1 className="qe__title">Quick Entry</h1>
                    <label className="qe__sort-toggle">
                        <input type="checkbox" checked={sortByAttention} onChange={onToggleSort} />
                        Needs attention first
                    </label>
                </div>
                <p className="qe__summary">
                    {attn > 0 ? (
                        <>
                            <strong>{ready}</strong> ready to confirm{" - "}
                            <span className="qe__summary--attn">{attn} need your attention</span>
                        </>
                    ) : (
                        `All ${props.rows.length} ready to submit`
                    )}
                </p>
            </div>

            <ul className="qe-row-list">
                {numberedRows.map(row => {
                    const customContent = props.renderRowContent?.(row);
                    const icon = getQuickEntryRowIcon(row);
                    return (
                        <li
                            key={row.id}
                            className="qe-row"
                            data-status={row.status}
                            data-row-id={row.id}
                        >
                            <span className="qe-row__badge">{row.badgeNumber}</span>
                            {icon ? (
                                <span className={`qe-row__icon ${icon.className}`} aria-label={icon.ariaLabel}>
                                    {icon.svg}
                                </span>
                            ) : null}
                            {customContent != null ? (
                                customContent
                            ) : (
                                <>
                                    <span className="qe-row__label">{row.label}</span>
                                    <span className="qe-row__value">{row.value}</span>
                                    {row.isAiPreSelected ? <span className="qe-chip--ai">AI pre-selected</span> : null}
                                </>
                            )}
                            {props.renderRowAdornment ? props.renderRowAdornment(row) : null}
                        </li>
                    );
                })}
            </ul>

            {props.footer}
        </div>
    );
};

export default QuickEntryFieldGroupList;
